import React, { useState, useEffect } from "react";
import { CircularLoading } from "../molecules/loading/CircularLoading";
import { LineUsageGuide } from "../pages/error/LineUsageGuide";
import { useSwrNoAuth } from "../../hooks/swr/useSwrNoAuth";
import { useFetchLiffIdCustomerByRestaurantId } from "../../hooks/externalApi/line/useFetchLiffIdCustomerByRestaurantId";
import { LineAccountRegister } from "../pages/externalApi/line/LineAccountRegister";
import { EditCustomer } from "../pages/externalApi/line/EditCustomer";
import { useFetchLineProfile } from "../../hooks/externalApi/line/useFetchLineProfile";
import { lineAccountIdState } from "../../store/fetchedData/lineAccountIdState";
import { useSetRecoilState } from "recoil";

type LiffLoginProps = {
  restaurantId: string;
  liffType: string;
  children: React.ReactNode;
}

export const LiffLoginProcess: React.FC<LiffLoginProps> = ({restaurantId, liffType, children}) => {
  const { liffId, isLoading, error } = useFetchLiffIdCustomerByRestaurantId(restaurantId, liffType);

  if (error?.response?.status === 404 || error?.response?.status === 400) {
    return <LineUsageGuide />;
  }

  if (!liffId || isLoading) {
    return <CircularLoading />;
  }

  try {
    return (
        <LineProfileFetcher restaurantId={restaurantId} liffId={liffId}>
          {children}
        </LineProfileFetcher>
    );
  } catch (err) {
    console.error("LIFFログインプロセスでエラーが発生しました:", err);
    return <LineUsageGuide />;
  }
};

type Profile = {
  userId: string;
  displayName: string;
  pictureUrl?: string;
}

const LineProfileFetcher: React.FC<{
  restaurantId: string,
  liffId: string,
  children: React.ReactNode
}> = ({restaurantId, liffId, children}) => {
  const profile = useFetchLineProfile(liffId);

  try {
    if (!profile) {
      return <CircularLoading />;
    }

    return (
        <ExistsLineAccountChecker restaurantId={restaurantId} profile={profile}>
          {children}
        </ExistsLineAccountChecker>
    );
  } catch (err) {
    console.error("LINEプロファイル取得エラー:", err);
    return <LineUsageGuide />;
  }
};

const ExistsLineAccountChecker: React.FC<{
  restaurantId: string,
  profile: Profile,
  children: React.ReactNode
}> = ({restaurantId, profile, children}) => {
  const setLineAccountId = useSetRecoilState(lineAccountIdState);
  const { data: lineAccountId, isLoading, error } = useSwrNoAuth<string>(
      `external_api/restaurants/${restaurantId}/line-accounts/${profile.userId}/line-account-id/`
  );

  try {
    if (error?.response?.status === 404) {
      return <LineAccountRegister restaurantId={restaurantId} profile={profile} />;
    }

    if (isLoading || !lineAccountId) {
      return <CircularLoading />;
    }

    try {
      setLineAccountId(lineAccountId);
    } catch (stateError) {
      console.error("Recoilステート更新エラー:", stateError);
      return <LineUsageGuide />;
    }

    return (
        <ShouldAnswerFirstSurveyChecker restaurantId={restaurantId} lineAccountId={lineAccountId}>
          {children}
        </ShouldAnswerFirstSurveyChecker>
    );
  } catch (err) {
    console.error("LINEアカウント確認エラー:", err);
    return <LineUsageGuide />;
  }
};

const ShouldAnswerFirstSurveyChecker: React.FC<{
  restaurantId: string,
  lineAccountId: string,
  children: React.ReactNode
}> = ({restaurantId, lineAccountId, children}) => {
  const fetchKey = `external_api/restaurants/${restaurantId}/line-accounts/${lineAccountId}/has-answered-first-survey/`;
  const { data: hasAnsweredFirstSurvey, isLoading: hasAnsweredFirstSurveyIsLoading } = useSwrNoAuth<boolean>(fetchKey);
  const { data: isFirstSurvey, isLoading: isFirstSurveyIsLoading } = useSwrNoAuth<boolean>(
      `menu/restaurants/${restaurantId}/first-survey/`
  );

  try {
    if (hasAnsweredFirstSurveyIsLoading || isFirstSurveyIsLoading) {
      return <CircularLoading />;
    }

    if (hasAnsweredFirstSurvey === false && isFirstSurvey === true) {
      return <EditCustomer mutateKey={fetchKey} lineAccountId={lineAccountId} />;
    }

    return <>{children}</>;
  } catch (err) {
    console.error("アンケート状態確認エラー:", err);
    return <LineUsageGuide />;
  }
};